import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, FileText, Smartphone, ShieldAlert, HeartPulse, Briefcase } from 'lucide-react';
import Contact from '../components/Contact';

const Careers = () => {
  return (
    <div className="bg-[#050505] min-h-screen text-white font-inter">
      {/* Header / Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#050505]/60 to-[#050505] z-10"></div>
          <img 
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_imvula-refresh/artifacts/z7aiuygz_Untitled-2.webp" 
            alt="Careers Background"
            className="w-full h-full object-cover opacity-60 mix-blend-luminosity"
          />
        </div>
        
        <div className="container mx-auto px-6 relative z-20">
          <div className="max-w-4xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-6 mb-8"
            >
              <span className="text-[#FFD700] font-space-grotesk tracking-widest text-sm uppercase flex items-center gap-4">
                <span className="w-12 h-[1px] bg-[#FFD700]"></span>
                Careers & Portal
              </span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-5xl md:text-7xl font-space-grotesk font-bold uppercase tracking-tighter leading-[0.9] mb-8"
            >
              Join the Ranks of <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FFD700] to-yellow-600">
                Absolute Protection.
              </span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl"
            >
              Whether you're looking to start your journey with iMvula or you're an existing employee seeking resources, find everything you need below.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Recruitment Section */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6 }}
              className="flex flex-col gap-8"
            >
              <div>
                <h2 className="text-3xl font-space-grotesk font-bold uppercase tracking-tight mb-4 flex items-center gap-3">
                  <Briefcase className="text-[#FFD700]" size={28} />
                  Recruitment
                </h2>
                <p className="text-gray-400 font-light leading-relaxed">
                  Apply today using our new iMvula Recruitment Portal or via the iMvula Recruitment App on the Google Play Store.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <a 
                  href="https://imvula.oraxsdi.com/iMvula-Recruitment/webhtml/134"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-[#0a0a0a] border border-white/10 hover:border-[#FFD700]/50 rounded-xl p-8 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <ArrowUpRight className="absolute top-6 right-6 text-gray-600 group-hover:text-[#FFD700] transition-colors" size={20} />
                  <Briefcase className="text-gray-400 mb-6 group-hover:text-[#FFD700] transition-colors" size={32} />
                  <h3 className="text-xl font-space-grotesk font-bold text-white mb-2">Web Portal</h3>
                  <p className="text-sm text-gray-500">Access our online recruitment portal to submit your application directly.</p>
                </a>

                <a 
                  href="https://play.google.com/store/apps/details?id=com.oraxsdi.imvularecruitment"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block bg-[#0a0a0a] border border-white/10 hover:border-[#FFD700]/50 rounded-xl p-8 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <ArrowUpRight className="absolute top-6 right-6 text-gray-600 group-hover:text-[#FFD700] transition-colors" size={20} />
                  <Smartphone className="text-gray-400 mb-6 group-hover:text-[#FFD700] transition-colors" size={32} />
                  <h3 className="text-xl font-space-grotesk font-bold text-white mb-2">Mobile App</h3>
                  <p className="text-sm text-gray-500">Download the iMvula Recruitment app from the Google Play Store.</p>
                </a>
              </div>

              {/* Anti-fraud warning */}
              <div className="bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl p-6 flex items-start gap-4 mt-4">
                <ShieldAlert className="text-[#FFD700] shrink-0 mt-1" size={24} />
                <div>
                  <h4 className="text-[#FFD700] font-bold font-space-grotesk tracking-wide mb-1">Anti-Fraud Warning</h4>
                  <p className="text-sm text-gray-300 font-light leading-relaxed">
                    iMvula will <strong>never</strong> ask for any form of payment or fees during the recruitment process. Please remain vigilant against recruitment scams. Any request for money from any person purporting to represent iMvula must be reported to info@imvula.net
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Employee Zone Section */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col gap-8 lg:pl-12 lg:border-l border-white/10"
            >
              <div>
                <h2 className="text-3xl font-space-grotesk font-bold uppercase tracking-tight mb-4 flex items-center gap-3">
                  <FileText className="text-[#FFD700]" size={28} />
                  Employee Zone
                </h2>
                <p className="text-gray-400 font-light leading-relaxed">
                  Important resources, policies, and documents for current iMvula employees.
                </p>
              </div>

              <div className="flex flex-col gap-4">
                <a 
                  href="https://www.imvula.net/_files/ugd/5edb16_c67e67db226d44cda29ba4052157f68e.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between p-6 bg-[#0a0a0a] border border-white/10 hover:border-[#FFD700]/50 rounded-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-6">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#FFD700]/10 transition-colors">
                      <FileText className="text-gray-400 group-hover:text-[#FFD700] transition-colors" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-space-grotesk font-bold text-white mb-1">Code of Conduct</h3>
                      <p className="text-xs text-gray-500 uppercase tracking-widest">PDF Document</p>
                    </div>
                  </div>
                  <ArrowUpRight className="text-gray-600 group-hover:text-[#FFD700] transition-colors" size={20} />
                </a>

                <a 
                  href="https://nbcpsshealth.co.za/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between p-6 bg-[#0a0a0a] border border-white/10 hover:border-[#FFD700]/50 rounded-xl transition-all duration-300"
                >
                  <div className="flex items-center gap-6">
                    <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-[#FFD700]/10 transition-colors">
                      <HeartPulse className="text-gray-400 group-hover:text-[#FFD700] transition-colors" size={24} />
                    </div>
                    <div>
                      <h3 className="text-lg font-space-grotesk font-bold text-white mb-1">Hospital Cover</h3>
                      <p className="text-xs text-gray-500 uppercase tracking-widest">External Portal</p>
                    </div>
                  </div>
                  <ArrowUpRight className="text-gray-600 group-hover:text-[#FFD700] transition-colors" size={20} />
                </a>
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Shared Contact Section */}
      <div className="border-t border-white/5">
        <Contact />
      </div>
    </div>
  );
};

export default Careers;
